import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Field } from '../../components/ui/Field';
import { Icon } from '../../components/ui/Icon';
import { ErrorNote } from '../../components/ui/misc';
import { ApiError } from '../../lib/api/errors';
import { authApi } from './api';
import { ACCEPT_PASSWORD_MIN, acceptProblem } from './acceptInvitation';
import { AuthLayout } from './LoginPage';

/**
 * Re-design T22 — where an invitation's one-time link lands (`/accept-invitation?token=…`):
 * the invitee chooses a password, the account is created active, and they sign in.
 * The token is single-use and expires 72 hours after it was issued.
 */
export function AcceptInvitationPage() {
  const token = useSearchParams()[0].get('token') ?? '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  if (!token) {
    return (
      <AuthLayout>
        <div className="mb-2 flex justify-center">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-alert-soft text-alert">
            <Icon name="link_off" className="text-[22px]" />
          </div>
        </div>
        <h1 className="text-center text-xl font-semibold text-ink">Invitation link incomplete</h1>
        <p className="mt-2 text-center text-sm text-ink-muted">
          This page needs the full link from your invitation. Ask your administrator to send you a new one.
        </p>
      </AuthLayout>
    );
  }

  if (done) {
    return (
      <AuthLayout>
        <div className="mb-2 flex justify-center">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-positive-soft text-positive">
            <Icon name="check_circle" className="text-[22px]" />
          </div>
        </div>
        <h1 className="text-center text-xl font-semibold text-ink">Your account is ready</h1>
        <p className="mt-2 text-center text-sm text-ink-muted">Your password is set. Sign in with your email address and the password you just chose.</p>
        <div className="mt-6">
          <Button asChild className="w-full">
            <Link to="/login">Continue to sign in</Link>
          </Button>
        </div>
      </AuthLayout>
    );
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const problem = acceptProblem(password, confirm);
    if (problem) {
      setError(problem);
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await authApi.acceptInvitation(token, password);
      setDone(true);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.status === 409
            ? 'An account with this email already exists. Try signing in, or ask for a password reset.'
            : 'This invitation link is invalid, has expired, or was already used. Ask your administrator to send a new one.'
          : 'Could not reach the server. Check your connection and try again.',
      );
      setBusy(false);
    }
  }

  return (
    <AuthLayout badge="Single-use invitation link">
      <h1 className="text-xl font-semibold text-ink">Welcome to HR Clock</h1>
      <p className="mt-1 text-sm text-ink-muted">Choose a password to finish setting up your account.</p>
      <form onSubmit={onSubmit} className="mt-6 flex flex-col gap-4" noValidate>
        {error && <ErrorNote>{error}</ErrorNote>}
        <Field
          label="New password"
          type={show ? 'text' : 'password'}
          autoComplete="new-password"
          required
          icon={<Icon name="key" className="text-[18px]" />}
          labelExtra={<span className="text-[11px] text-ink-muted">Min. {ACCEPT_PASSWORD_MIN} characters</span>}
          endAdornment={
            <button
              type="button"
              onClick={() => setShow((v) => !v)}
              aria-label={show ? 'Hide typed characters' : 'Show typed characters'}
              className="px-2.5 text-ink-subtle transition-colors hover:text-ink"
            >
              <Icon name={show ? 'visibility_off' : 'visibility'} className="text-[18px]" />
            </button>
          }
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <Field
          label="Confirm new password"
          type={show ? 'text' : 'password'}
          autoComplete="new-password"
          required
          icon={<Icon name="lock_reset" className="text-[18px]" />}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
        />
        <Button type="submit" loading={busy} className="mt-1 w-full">
          <span>Create my account</span>
          <Icon name="arrow_forward" className="text-[18px]" />
        </Button>
      </form>
    </AuthLayout>
  );
}
